import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Table, Button } from 'reactstrap';
import i18n from '@/utils/i18n';
import Fuse from 'fuse.js';
import { LayoutStoreInstance } from '../../../LayoutStore';
import { useUpdateInvoiceCollectionMutation } from '../../../graphql/mutations/updateInvoiceCollection.generated';
import AutocompletePopover from '@/components/AutocompletePopover';

type StoreLite = { id: string; name: string };

export function ManageExcludedStores({
  invoiceCollectionId,
  excludedStores,
  linkedStores,
  refetch,
}: {
  invoiceCollectionId: string;
  excludedStores: StoreLite[];
  linkedStores: StoreLite[];
  refetch: () => Promise<any>;
}) {
  const [query, setQuery] = useState('');
  const [updateInvoiceCollection] = useUpdateInvoiceCollectionMutation();

  const excludableStores = useMemo(() => {
    const excludedIds = new Set(excludedStores.map((s) => s.id));
    return linkedStores.filter((s) => !excludedIds.has(s.id));
  }, [linkedStores, excludedStores]);

  const fuse = useMemo(
    () =>
      new Fuse(excludableStores, {
        keys: ['name', 'id'],
        threshold: 0.3,
      }),
    [excludableStores],
  );
  const excludableSearchResults = query
    ? fuse.search(query).map((r) => r.item)
    : [];

  const options = excludableSearchResults.map((s) => ({
    id: s.id,
    label: `${s.name} (${s.id})`,
  }));

  const includeStore = async (storeId: string) => {
    const current = excludedStores.map((s) => s.id);
    try {
      await updateInvoiceCollection({
        variables: {
          id: invoiceCollectionId,
          payload: { excluded_store_ids: current.filter((x) => x !== storeId) },
        },
      });
      await refetch();
      LayoutStoreInstance.setSuccess(i18n.t('Common:saved'));
    } catch (err: any) {
      LayoutStoreInstance.setError(
        i18n.t('Common:error') + ': ' + (err?.message || 'unknown'),
      );
    }
  };

  const excludeStore = async (storeId: string) => {
    const current = excludedStores.map((s) => s.id);
    if (current.includes(storeId)) return;
    try {
      await updateInvoiceCollection({
        variables: {
          id: invoiceCollectionId,
          payload: { excluded_store_ids: [...current, storeId] },
        },
      });
      await refetch();
      LayoutStoreInstance.setSuccess(i18n.t('Common:saved'));
    } catch (err: any) {
      LayoutStoreInstance.setError(
        i18n.t('Common:error') + ': ' + (err?.message || 'unknown'),
      );
    }
  };

  return (
    <>
      {/* Excluded Stores */}
      <h3>{i18n.t('InvoiceCollection:titleExcludedStores')}</h3>
      {excludedStores.length > 0 ? (
        <Table striped bordered>
          <thead>
            <tr>
              <th>{i18n.t('Common:store')}</th>
              <th>{i18n.t('Common:id')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {excludedStores.map((s) => (
              <tr key={s.id}>
                <td>
                  <Link to={`/pickpack/stores/${s.id}`}>{s.name}</Link>
                </td>
                <td
                  onClick={() => {
                    navigator.clipboard.writeText(s.id);
                    LayoutStoreInstance.setSuccess(i18n.t('Common:copied'));
                  }}
                >
                  {s.id}
                </td>
                <td>
                  <Button
                    style={{ marginLeft: '10px' }}
                    className="btn btn-sm btn-success"
                    onClick={() => includeStore(s.id)}
                  >
                    {i18n.t('InvoiceCollection:include')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p>{i18n.t('Common:none')}</p>
      )}

      <br />

      {/* Find stores to exclude (fuzzy search) */}
      <h3>{i18n.t('InvoiceCollection:titleExcludableStores')}</h3>
      <AutocompletePopover
        value={query}
        onChange={setQuery}
        placeholder={
          i18n.t('InvoiceCollection:searchExcludableStores') as string
        }
        options={options}
        onOptionClick={async (opt) => {
          await excludeStore(opt.id);
        }}
        opened={Boolean(query) && options.length > 0}
        clearOnSelect
      />
    </>
  );
}
